import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useOrderGroup } from "@/api/hooks/use-orders";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { Button } from "@/components/ui/button";
import {
  OrderHeaderSection,
  OrderItemsSection,
  OrderSummarySection,
  OrderAddressSection,
  OrderPaymentSection,
} from "@/features/orders";
import { Package, ShoppingBag } from "lucide-react";
import { useOrdersStore } from "@/features/orders/store/orders-store";
import { useToast } from "@/features/toast";
import { fashionPaths } from "@/modules";

const OrderSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const { orderGroupId } = useParams<{ orderGroupId: string }>();
  const navigate = useNavigate();
  const { setActiveTab } = useOrdersStore();
  const { success } = useToast();
  // Auto-redirect to orders page after 15 seconds if from checkout
  React.useEffect(() => {
    success(t("checkout.paymentSuccess"), {
      duration: 15000,
      actions: [
        {
          label: t("orders.backToOrders"),
          action: () => navigate(`/orders`, { replace: true }),
        },
        {
          label: t("orders.continueShopping"),
          action: () => navigate(fashionPaths.shop(), { replace: true }),
        },
      ],
    });
    const timer = setTimeout(() => {
      setActiveTab("orders");
      navigate("/orders", { replace: true });
    }, 15000); // 15 seconds

    return () => clearTimeout(timer);
  }, [navigate, setActiveTab]);

  // Fetch order group data
  const {
    data: orderGroupResponse,
    isLoading,
    isError,
    error,
  } = useOrderGroup(orderGroupId || null);

  // Extract orders and detail from response
  const orders = orderGroupResponse?.data?.orders || [];
  const orderGroupDetail = orderGroupResponse?.data?.detail;

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto bg-bg-primary dark:bg-bg-primary">
        <div className="max-w-6xl mx-auto py-8 px-4 md:px-8">
          <div className="flex justify-center items-center py-16">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !orderGroupId || orders.length === 0) {
    return (
      <div className="h-full overflow-y-auto bg-bg-primary dark:bg-bg-primary">
        <div className="max-w-6xl mx-auto py-8 px-4 md:px-8">
          <div className="text-center py-16">
            <h1 className="text-2xl font-semibold text-text-primary dark:text-text-primary mb-2">
              {t("orders.errorTitle")}
            </h1>
            <p className="text-text-tertiary dark:text-text-tertiary mb-6">
              {error?.message || t("orders.errorMessage")}
            </p>
            <Button asChild variant="darkPrimary">
              <Link to="/">{t("orders.backToHome")}</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Get order group ID for header display
  const orderGroupIdDisplay =
    orderGroupDetail?.orderGroupId || orders[0]?.orderGroupId || orderGroupId;

  const currency = orderGroupDetail?.currency || orders[0]?.currency || "TRY";

  return (
    <div className="h-full overflow-y-auto bg-bg-primary dark:bg-gray-900">
      {/* Main Content */}
      <div className="px-4 md:px-[46px] py-4 md:py-6">
        <div className="w-full max-w-5xl mx-auto">
          {/* Order Header Section */}
          <OrderHeaderSection orderGroupId={orderGroupIdDisplay} />

          {/* Order Items Section */}
          <div className="mt-8">
            <OrderItemsSection orders={orders} />
          </div>

          {/* Order Summary & Details Section */}
          <div className="mt-8 rounded-[12px] overflow-hidden">
            {/* Delivery Address */}
            <OrderAddressSection orderGroupDetail={orderGroupDetail} />

            {/* Divider */}
            <div className="h-px bg-[#e3e8ef] dark:bg-gray-700" />

            {/* Payment */}
            <OrderPaymentSection orderGroupDetail={orderGroupDetail} />

            <div className="h-px bg-[#e3e8ef] dark:bg-gray-700" />
            {/* Order Summary - Top */}
            <OrderSummarySection
              orderGroupDetail={orderGroupDetail}
              currency={currency}
            />

            {/* Divider */}
          </div>

          {/* Divider */}
          <div className="h-px bg-[#e3e8ef] dark:bg-gray-700 mt-8" />

          {/* Action Buttons */}
          <div className="mt-6 md:mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0">
            {/* Contact Us Link */}
            <div className="text-xs md:text-sm text-text-primary dark:text-text-primary text-center md:text-left">
              <span>{t("orders.needHelp")} </span>
              <button
                onClick={() => {
                  // TODO: Implement contact us action
                  console.log("Contact us clicked");
                }}
                className="font-semibold underline hover:text-text-secondary dark:hover:text-text-secondary transition-colors"
              >
                {t("orders.contactUs")}
              </button>
            </div>

            {/* Continue Shopping Button */}
            <div className="flex flex-col md:flex-row gap-3 sm:gap-4 ">
              <Button asChild variant="outline" className="w-full md:w-auto">
                <Link to="/orders" replace>
                  <Package className="w-4 h-4" />
                  {t("orders.backToOrders")}
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full md:w-auto">
                <Link to={fashionPaths.shop()} replace>
                  <ShoppingBag className="w-4 h-4" />
                  {t("orders.continueShopping")}
                </Link>
              </Button>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-[#e3e8ef] dark:bg-gray-700 mt-6 md:mt-8" />

          {/* Footer Links */}
          <div className="mt-6 md:mt-8 flex flex-wrap items-center justify-center md:justify-between gap-3 md:gap-0 text-xs md:text-sm text-text-primary dark:text-text-primary">
            <button
              onClick={() => {
                // TODO: Implement navigation to refund policy
                console.log("Refund Policy clicked");
              }}
              className="hover:text-text-secondary dark:hover:text-text-secondary transition-colors"
            >
              {t("orders.refundPolicy")}
            </button>
            <button
              onClick={() => {
                // TODO: Implement navigation to privacy policy
                console.log("Privacy Policy clicked");
              }}
              className="hover:text-text-secondary dark:hover:text-text-secondary transition-colors"
            >
              {t("orders.privacyPolicy")}
            </button>
            <button
              onClick={() => {
                // TODO: Implement navigation to terms of service
                console.log("Terms of Service clicked");
              }}
              className="hover:text-text-secondary dark:hover:text-text-secondary transition-colors"
            >
              {t("orders.termsOfService")}
            </button>
            <button
              onClick={() => {
                // TODO: Implement navigation to subscription policy
                console.log("Subscription Policy clicked");
              }}
              className="hover:text-text-secondary dark:hover:text-text-secondary transition-colors"
            >
              {t("orders.subscriptionPolicy")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
